import {
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Link as LinkIcon,
  Mail,
  MapPin,
  MessageCircle,
  Music2,
  Phone,
  Twitter,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion } from 'framer-motion'
import { NavLink } from 'react-router-dom'

import logoProdamac from '@/assets/logo-prodamac.webp'
import { Container } from '@/components/ui/Container'
import { getLocalizedPath } from '@/content/i18n'
import { navItems, siteContent } from '@/content/siteContent'
import { useMotionPreference } from '@/hooks/useMotionPreference'
import { usePublicSiteBundle } from '@/hooks/usePublicSiteBundle'
import { EASE, footerBlockReveal, footerColumnReveal, viewportRevealTight } from '@/lib/motion'
import { getLocalizedValue } from '@/lib/utils'
import type { Locale } from '@/types/site'

type SiteFooterProps = {
  locale: Locale
}

/* ── social icon registry ────────────────────────────────── */
const SOCIAL_ICON_MAP: Record<string, LucideIcon> = {
  instagram: Instagram,
  tiktok: Music2,
  facebook: Facebook,
  linkedin: Linkedin,
  x: Twitter,
  twitter: Twitter,
  snapchat: Globe,
  linktree: LinkIcon,
  whatsapp: MessageCircle,
}

const SOCIAL_LABELS: Record<string, { en: string; ar: string }> = {
  instagram: { en: 'Instagram', ar: 'انستجرام' },
  tiktok: { en: 'TikTok', ar: 'تيك توك' },
  facebook: { en: 'Facebook', ar: 'فيس بوك' },
  linkedin: { en: 'LinkedIn', ar: 'لينكد إن' },
  x: { en: 'X', ar: 'إكس' },
  snapchat: { en: 'Snapchat', ar: 'سناب شات' },
  linktree: { en: 'Linktree', ar: 'لينك تري' },
  whatsapp: { en: 'WhatsApp', ar: 'واتساب' },
}

const linkHoverUnderline =
  'inline-block [background-size:0_2px] [background-image:linear-gradient(currentColor,currentColor)] [background-position:0_100%] [background-repeat:no-repeat] transition-[background-size,color] duration-300 hover:[background-size:100%_2px]'

export function SiteFooter({ locale }: SiteFooterProps) {
  const reduceMotion = useMotionPreference()
  const bundle = usePublicSiteBundle()
  const footer = siteContent.footer
  const year = new Date().getFullYear()
  const copyrightText = getLocalizedValue(footer.copyright, locale).replace('{year}', String(year))

  /* ── Resolve social links: prefer API data, filter empty ─ */
  const apiSocial = (() => {
    const int = bundle.data?.site_config?.integrations
    if (!int || typeof int !== 'object') return null
    const s = (int as Record<string, unknown>).social
    if (!s || typeof s !== 'object') return null
    return s as Record<string, string>
  })()

  type SocialLink = { id: string; url: string; label: { en: string; ar: string } }

  const socialLinks: SocialLink[] = (() => {
    if (apiSocial) {
      // Build from API — only include non-empty entries
      return Object.entries(apiSocial)
        .filter(([, url]) => typeof url === 'string' && url.trim() !== '')
        .filter(([key]) => key !== 'whatsapp') // WhatsApp has its own floating button
        .map(([key, url]) => ({
          id: key,
          url: url as string,
          label: SOCIAL_LABELS[key] ?? { en: key, ar: key },
        }))
    }
    // Fallback to static siteContent
    return footer.social.filter((s) => s.url.trim() !== '')
  })()

  return (
    <motion.footer
      className="border-t border-slate-200 bg-slate-50 py-16 text-slate-800"
      initial={reduceMotion ? false : 'hidden'}
      whileInView={reduceMotion ? undefined : 'visible'}
      viewport={viewportRevealTight}
      variants={footerBlockReveal}
    >
      <Container>
        <div className="grid gap-10 sm:gap-12 lg:grid-cols-[1.35fr_0.75fr_0.75fr_1fr]">
          {/* Company */}
          <motion.div
            custom={0}
            variants={reduceMotion ? undefined : footerColumnReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.12 }}
            className="max-w-sm"
          >
            <img
              src={logoProdamac}
              alt="Pro Damac General Trading"
              loading="lazy"
              className="h-10 w-auto max-w-[180px] object-contain"
            />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              {getLocalizedValue(siteContent.company.legalName, locale)}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {getLocalizedValue(footer.shortAbout, locale)}
            </p>
          </motion.div>

          {/* Services */}
          <motion.div
            custom={1}
            variants={reduceMotion ? undefined : footerColumnReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.12 }}
          >
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {locale === 'ar' ? 'الخدمات' : 'Services'}
            </h3>
            <nav className="mt-4 flex flex-col gap-2.5" aria-label={locale === 'ar' ? 'الخدمات' : 'Services'}>
              {(bundle.data?.services_preview ?? []).slice(0, 5).map((service) => (
                <NavLink
                  key={service.id}
                  to={`${getLocalizedPath(locale, 'services')}/${service.slug}`}
                  className={`text-sm text-slate-600 hover:text-brand ${linkHoverUnderline}`}
                >
                  {locale === 'ar' ? service.title_ar : service.title_en}
                </NavLink>
              ))}
            </nav>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            custom={2}
            variants={reduceMotion ? undefined : footerColumnReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.12 }}
          >
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {locale === 'ar' ? 'روابط سريعة' : 'Quick links'}
            </h3>
            <nav className="mt-4 flex flex-col gap-2.5" aria-label={locale === 'ar' ? 'روابط سريعة' : 'Quick links'}>
              {navItems.map((item) => (
                <NavLink
                  key={item.key}
                  to={getLocalizedPath(locale, item.slug)}
                  className={`text-sm text-slate-600 hover:text-brand ${linkHoverUnderline}`}
                >
                  {getLocalizedValue(item.label, locale)}
                </NavLink>
              ))}
            </nav>
          </motion.div>

          {/* Contact */}
          <motion.div
            custom={3}
            variants={reduceMotion ? undefined : footerColumnReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.12 }}
          >
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {locale === 'ar' ? 'معلومات التواصل' : 'Contact'}
            </h3>
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <p className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
                <span className="leading-6">{getLocalizedValue(footer.address, locale)}</span>
              </p>
              <div className="flex flex-col gap-1.5">
                {footer.phones.map((phone, i) => (
                  <a
                    key={i}
                    href={`tel:${phone.en.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 transition-colors hover:text-brand"
                  >
                    <Phone className="h-4 w-4 shrink-0 text-brand" aria-hidden />
                    <span dir="ltr">{locale === 'ar' ? phone.ar : phone.en}</span>
                  </a>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                {footer.emails.map((email) => (
                  <a
                    key={email.value}
                    href={`mailto:${email.value}`}
                    className="flex items-center gap-3 text-slate-600 transition-colors hover:text-brand"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-brand" aria-hidden />
                    <span>{email.value}</span>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <motion.div
          className="mt-12 border-t border-slate-200 pt-8"
          initial={reduceMotion ? false : { opacity: 0 }}
          whileInView={reduceMotion ? undefined : { opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.15, duration: 0.35 }}
        />

        {/* Bottom */}
        <motion.div
          className="flex flex-col items-center justify-between gap-4 sm:flex-row"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.2, duration: 0.4, ease: EASE }}
        >
          <p className="text-center text-sm text-slate-500 sm:text-start">
              {copyrightText}
              <span className="mx-1.5 text-slate-300">|</span>
              {locale === 'ar' ? 'تطوير' : 'Developed by'}{' '}
              <a
                href="https://tarawud.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-slate-600 transition-colors hover:text-brand"
              >
                Tarawud
              </a>
            </p>
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-3">
              {socialLinks.map((item) => {
                const Icon = SOCIAL_ICON_MAP[item.id] ?? LinkIcon
                const label = getLocalizedValue(item.label, locale)
                return (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="rounded-full p-2 text-slate-500 transition-all duration-300 hover:scale-110 hover:bg-slate-200/80 hover:text-brand"
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </a>
                )
              })}
            </div>
          )}
        </motion.div>
      </Container>
    </motion.footer>
  )
}
